import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Chip, TextField, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import { zkApi } from "./zkApi";
import { DECISION_META, RESOLUTION_META, apiError, chipSx, metaFor, reasonLabel } from "./zkStatusStyles";
import { backButtonSx } from "../hpcAdminStatusStyles";

const isoDay = (d) => {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

export default function ZkRefusedScans() {
  const [to, setTo] = useState(() => isoDay(new Date()));
  const [from, setFrom] = useState(() => isoDay(new Date(Date.now() - 6 * 86400000)));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await zkApi("get", "/hpc/admin/zk/refused", { params: { from, to } });
      setRows((res.data?.items || []).map((r, i) => ({ ...r, _id: `${r.serial_number}-${r.pin}-${r.time}-${i}` })));
      setError("");
    } catch (e) {
      setError(apiError(e, "Could not load refused scans"));
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    { field: "time", headerName: "Time", width: 160 },
    { field: "pin", headerName: "PIN", width: 80 },
    { field: "device_name", headerName: "Name", width: 150, valueGetter: (v) => v || "-" },
    {
      field: "member", headerName: "Member", flex: 1, minWidth: 220, sortable: false,
      renderCell: ({ row }) => (row.member
        ? <span>{row.member.full_name} <span style={{ opacity: 0.7 }}>({row.member.membership_id})</span></span>
        : (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Chip size="small" label={metaFor(RESOLUTION_META, row.resolution).label}
              sx={chipSx(metaFor(RESOLUTION_META, row.resolution).color)} />
          </Box>
        )),
    },
    {
      field: "desired", headerName: "TEBA decision", width: 130,
      renderCell: ({ value }) => (value ? (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Chip size="small" label={metaFor(DECISION_META, value).label} sx={chipSx(metaFor(DECISION_META, value).color)} />
        </Box>
      ) : <span style={{ opacity: 0.6 }}>not evaluated</span>),
    },
    { field: "reason", headerName: "Why", flex: 1, minWidth: 180, valueGetter: (v) => (v ? reasonLabel(v) : "-") },
    { field: "verify_mode", headerName: "Method", width: 100, valueGetter: (v) => v || "-" },
  ];

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
        <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={from}
          onChange={(e) => setFrom(e.target.value)} />
        <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={to}
          onChange={(e) => setTo(e.target.value)} />
        <Typography variant="body2">{rows.length} refused scan(s)</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="outlined" sx={backButtonSx} startIcon={<RefreshOutlinedIcon />} disabled={loading} onClick={load}>
          Reload
        </Button>
      </Box>
      <Box sx={{ height: "55vh" }}>
        <DataGrid rows={rows} columns={columns} loading={loading} getRowId={(r) => r._id} disableRowSelectionOnClick
          localeText={{ noRowsLabel: "No refused scans in this period" }} />
      </Box>
      <Typography variant="caption" sx={{ opacity: 0.7 }}>
        Scans the device refused at the door (access denied, rtlog event 23). They are never counted as attendance.
        Times are Sri Lanka time.
      </Typography>
    </Box>
  );
}
